import { ThemeEvents, CartAddEvent, DiscountUpdateEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { morphSection } from '@theme/section-renderer';

/**
 * @param {HTMLFormElement | null | undefined} form
 * @returns {Record<string, string>}
 */
function collectLineItemProperties(form) {
  const props = {};
  if (!form) return props;

  /** @param {string} key @param {FormDataEntryValue} value */
  function addProp(key, value) {
    const match = key.match(/^properties\[(.+)\]$/);
    if (!match) return;
    const trimmed = String(value || '').trim();
    if (trimmed) props[match[1]] = trimmed;
  }

  new FormData(form).forEach((value, key) => addProp(key, value));

  if (form.id) {
    document.querySelectorAll('[form="' + form.id + '"]').forEach(function (field) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) {
        return;
      }
      if (field.name) addProp(field.name, field.value);
    });
  }

  return props;
}

window.AndradePdpLineItem = window.AndradePdpLineItem || {};
window.AndradePdpLineItem.collectFromForm = collectLineItemProperties;

/**
 * @param {HTMLElement | null | undefined} root
 */
function clearKitValidation(root) {
  if (!root) return;
  const banner = root.querySelector('[data-md-kit-validation]');
  if (banner) banner.hidden = true;
  root.querySelectorAll('.md-pdp-field--error').forEach(function (el) {
    el.classList.remove('md-pdp-field--error');
  });
}

/**
 * @param {HTMLElement | null | undefined} root
 * @param {string} message
 */
function showKitValidation(root, message) {
  if (!root) return;
  const banner = root.querySelector('[data-md-kit-validation]');
  if (!banner) return;
  banner.textContent = message;
  banner.hidden = false;
  banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

/**
 * @param {HTMLFormElement | null | undefined} form
 * @param {HTMLElement | null | undefined} [root]
 * @returns {boolean}
 */
function validateFormAssociatedFields(form, root) {
  root = root || (form && form.closest('[data-md-pdp]'));
  if (!form || !root) return true;

  clearKitValidation(root);

  const message =
    root.dataset.kitValidationMessage ||
    'Selecione os tamanhos e grave o nome em cada aliança antes de comprar.';
  const formId = form.id;
  const seen = new Set();
  /** @type {HTMLElement[]} */
  const invalid = [];

  /**
   * @param {Element | null | undefined} field
   */
  function noteInvalid(field) {
    if (!field || !(field instanceof HTMLElement) || seen.has(field)) return;
    seen.add(field);
    const value = String(field.value || '').trim();
    if (!value) invalid.push(field);
  }

  if (formId) {
    document.querySelectorAll('[form="' + formId + '"]').forEach(function (field) {
      if (!(field instanceof HTMLElement)) return;
      if (field.matches('select[required], input[required], textarea[required]')) {
        noteInvalid(field);
      }
    });
  }

  root.querySelectorAll('[data-md-kit-sizes] select, [data-md-kit-extra-size] select').forEach(noteInvalid);
  root.querySelectorAll('[data-md-gravacao-fields] input[type="text"][required], [data-md-gravacao-input]').forEach(
    noteInvalid
  );

  if (root.dataset.requiresGravacao === 'true') {
    const gravacaoInputs = root.querySelectorAll('[data-md-gravacao-fields] input[type="text"], [data-md-gravacao-input]');
    if (!gravacaoInputs.length) {
      showKitValidation(root, root.dataset.gravacaoRequired || message);
      return false;
    }
  }

  if (invalid.length) {
    invalid.forEach(function (field) {
      const wrap = field.closest(
        '.md-pdp-kit-sizes__field, .md-pdp-gravacao__field, .variant-option, .md-pdp-kit-sizes'
      );
      if (wrap) wrap.classList.add('md-pdp-field--error');
    });

    invalid[0].focus();
    if (typeof invalid[0].reportValidity === 'function') invalid[0].reportValidity();
    showKitValidation(root, message);
    return false;
  }

  return validateSolitarioUpgrade(root);
}

/**
 * @param {HTMLElement | null | undefined} root
 * @returns {{ active: boolean, missing?: boolean, variantId?: number, title?: string }}
 */
function resolveUpgradeVariantId(selected, properties, wrap) {
  const fallback = Number(selected.getAttribute('data-variant-id') || '');
  const sizeKey =
    (wrap && wrap.getAttribute('data-size-prop')) ||
    '';
  const size = sizeKey && properties && properties[sizeKey] ? String(properties[sizeKey]).trim() : '';
  const raw = selected.getAttribute('data-size-map') || '';
  if (!size || !raw) return fallback;
  try {
    const map = JSON.parse(raw);
    const keys = [size, size.replace(/^0+/, '') || size];
    if (/^\d+$/.test(size) && size.length < 2) keys.push(size.padStart(2, '0'));
    for (let i = 0; i < keys.length; i += 1) {
      const id = map[keys[i]];
      if (id) return Number(id);
    }
  } catch (e) {
    /* ignore */
  }
  return fallback;
}

function getKitUpgradeWraps(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll('[data-md-kit-upgrade], [data-md-solitario-upgrade]')).filter(
    function (el, index, list) {
      return list.indexOf(el) === index;
    }
  );
}

function getUpgradeSelectionFromWrap(wrap) {
  if (!wrap) return { active: false, wrap: wrap };
  const toggle = wrap.querySelector('[data-md-solitario-upgrade-toggle]');
  if (!(toggle instanceof HTMLInputElement) || !toggle.checked) {
    return { active: false, wrap: wrap };
  }
  const selected = wrap.querySelector('[data-md-solitario-upgrade-option]:checked');
  if (!(selected instanceof HTMLInputElement)) return { active: true, missing: true, wrap: wrap };
  const variantId = Number(selected.getAttribute('data-variant-id') || '');
  const title = (selected.getAttribute('data-title') || '').trim();
  const priceCents = Number(selected.getAttribute('data-price-cents') || 0);
  const saleCents = Number(selected.getAttribute('data-sale-cents') || priceCents);
  if (!variantId || !title) return { active: true, missing: true, wrap: wrap };
  return {
    active: true,
    variantId: variantId,
    title: title,
    priceCents: priceCents,
    saleCents: saleCents,
    selectedEl: selected,
    wrap: wrap,
  };
}

function getKitUpgradeSelections(root) {
  return getKitUpgradeWraps(root).map(getUpgradeSelectionFromWrap);
}

function getSolitarioUpgradeSelection(root) {
  const selections = getKitUpgradeSelections(root);
  const firstActive = selections.find(function (s) {
    return s.active;
  });
  return firstActive || { active: false };
}

/**
 * @param {HTMLElement | null | undefined} root
 * @returns {boolean}
 */
function validateSolitarioUpgrade(root) {
  const selections = getKitUpgradeSelections(root);
  const missing = selections.find(function (s) {
    return s.active && s.missing;
  });
  if (!missing) return true;
  if (missing.wrap) missing.wrap.classList.add('md-pdp-field--error');
  showKitValidation(
    root,
    (root && root.dataset.solitarioUpgradeRequired) ||
      'Selecione o modelo do upgrade ou desmarque a opção.'
  );
  return false;
}

/**
 * @param {HTMLFormElement | null | undefined} form
 * @param {HTMLElement | null | undefined} root
 * @param {number|string} kitVariantId
 * @returns {{ ok: boolean, items?: Array<{id: number, quantity: number, properties?: Record<string, string>}>, error?: string }}
 */
function buildBuyNowItems(form, root, kitVariantId) {
  const id = Number(kitVariantId);
  if (!id) return { ok: false, error: 'variant' };

  const selections = getKitUpgradeSelections(root);
  if (
    selections.some(function (s) {
      return s.active && s.missing;
    })
  ) {
    return { ok: false, error: 'upgrade' };
  }

  const properties = collectLineItemProperties(form);
  const stock = root && root.querySelector('[data-md-stock]');
  if (stock) {
    const prazoKey = 'Prazo';
    if (!properties[prazoKey]) {
      const text = stock.getAttribute('data-production-text') || stock.textContent;
      if (text && String(text).trim()) properties[prazoKey] = String(text).trim();
    }
    if (!properties._andrade_prazo_dias) {
      const days = stock.getAttribute('data-production-days');
      if (days) properties._andrade_prazo_dias = String(days);
    }
  }
  selections.forEach(function (sel) {
    const wrap = sel.wrap;
    if (!sel.active || sel.missing || !wrap || !sel.title) return;
    const propName = wrap.getAttribute('data-upgrade-prop') || 'Upgrade';
    properties[propName] = sel.title;
    const giftProp = wrap.getAttribute('data-gift-prop') || 'Peça inclusa';
    const giftVal = wrap.getAttribute('data-gift-value') || 'Não enviar — substituído pelo upgrade';
    properties[giftProp] = giftVal;
    const discountProp = wrap.getAttribute('data-discount-prop') || 'Desconto upgrade';
    const discountVal = wrap.getAttribute('data-discount-value') || '';
    if (discountVal) properties[discountProp] = discountVal;
  });

  const item = { id: id, quantity: 1 };
  if (Object.keys(properties).length) item.properties = properties;
  const items = [item];
  if (window.AndradeGift && typeof window.AndradeGift.lineItem === 'function' && id !== Number(window.AndradeGift.variantId)) {
    items.push(window.AndradeGift.lineItem());
  }

  selections.forEach(function (sel) {
    const wrap = sel.wrap;
    if (!sel.active || sel.missing || !sel.variantId || !wrap) return;
    const upProps = {};
    const originProp = wrap.getAttribute('data-origin-prop') || 'Origem';
    const originVal = wrap.getAttribute('data-origin-value') || 'Upgrade do kit';
    upProps[originProp] = originVal;
    const kitProp = wrap.getAttribute('data-kit-prop') || 'Kit';
    const kitTitle = wrap.getAttribute('data-kit-title') || (root && root.dataset.productTitle) || '';
    if (kitTitle) upProps[kitProp] = kitTitle;
    const sizeKey =
      wrap.getAttribute('data-size-prop') ||
      (root && root.dataset.sizeSolitarioLabel) ||
      '';
    if (sizeKey && properties[sizeKey]) upProps[sizeKey] = properties[sizeKey];
    const upgradeId = sel.selectedEl
      ? resolveUpgradeVariantId(sel.selectedEl, properties, wrap)
      : sel.variantId;
    items.push({ id: upgradeId || sel.variantId, quantity: 1, properties: upProps });
  });

  return { ok: true, items: items };
}

window.AndradePdpLineItem.validateFormAssociatedFields = validateFormAssociatedFields;
window.AndradePdpLineItem.clearKitValidation = clearKitValidation;
window.AndradePdpLineItem.getSolitarioUpgradeSelection = getSolitarioUpgradeSelection;
window.AndradePdpLineItem.buildBuyNowItems = buildBuyNowItems;
window.AndradePdpLineItem.applyUpgradeDiscountCode = applyUpgradeDiscountCode;
window.AndradePdpLineItem.collectCartSectionIds = collectCartSectionIds;
window.AndradePdpLineItem.applyCartSections = applyCartSections;
window.AndradePdpLineItem.openCartDrawer = openCartDrawer;

function collectCartSectionIds() {
  const sectionIds = [];
  document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
    const id = el.getAttribute('data-section-id');
    if (id && sectionIds.indexOf(id) === -1) sectionIds.push(id);
  });
  return sectionIds;
}

function cartHasUpgradeLine(cart) {
  if (!cart || !Array.isArray(cart.items)) return false;
  for (let i = 0; i < cart.items.length; i += 1) {
    const props = cart.items[i].properties || {};
    const origin = String(props['Origem'] || props['Origin'] || '');
    if (/upgrade/i.test(origin)) return true;
  }
  return false;
}

function hasActiveUpgrade(root) {
  const selection = root ? getSolitarioUpgradeSelection(root) : { active: false };
  return Boolean(selection.active && !selection.missing);
}

function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
    return;
  }
  window.location.assign((window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart');
}

function closeCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.close === 'function') drawer.close();
}

function buildCartAddPayload(items) {
  const payload = { items: items };
  const sectionIds = collectCartSectionIds();
  if (sectionIds.length) {
    payload.sections = sectionIds.join(',');
    payload.sections_url = window.location.pathname;
  }
  return payload;
}

function applyCartSections(cart) {
  const sections = cart && cart.sections;
  if (!sections) return Promise.resolve();
  const ids = Object.keys(sections);
  return ids.reduce(function (chain, id) {
    return chain.then(function () {
      return morphSection(id, sections[id], { mode: 'hydration' }).catch(function () {
        return morphSection(id, sections[id], { mode: 'full' }).catch(function () {
          return null;
        });
      });
    });
  }, Promise.resolve());
}

function applyUpgradeDiscountCode(root) {
  if (!hasActiveUpgrade(root)) return Promise.resolve(null);

  const wrap = root && root.querySelector('[data-md-solitario-upgrade]');
  const code =
    (typeof window.AndradeUpgradeDiscountCode === 'string' && window.AndradeUpgradeDiscountCode.trim()) ||
    (wrap && (wrap.getAttribute('data-discount-code') || '').trim()) ||
    'up4T8B893VXB5FBG496gr0Na2FTde5K';
  if (!code) return Promise.resolve(null);

  const updateUrl =
    (window.Theme && Theme.routes && Theme.routes.cart_update_url) || '/cart/update.js';

  return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
    .then(function (res) {
      if (!res.ok) return null;
      return res.json();
    })
    .then(function (cart) {
      const selection = root ? getSolitarioUpgradeSelection(root) : { active: false };
      const hasUpgrade = Boolean(selection.active && !selection.missing) || cartHasUpgradeLine(cart);

      const existing = [];
      if (cart && Array.isArray(cart.discount_codes)) {
        cart.discount_codes.forEach(function (entry) {
          if (entry && entry.code && existing.indexOf(entry.code) === -1) {
            existing.push(entry.code);
          }
        });
      }
      var pixCode =
        (typeof window.AndradePixDiscountCode === 'string' && window.AndradePixDiscountCode) || 'ANDRADE-PIX5';
      var next = existing.filter(function (c) {
        return c !== code && c !== pixCode;
      });
      if (hasUpgrade) next.push(code);
      next.push(pixCode);

      const body = { discount: next.join(',') };
      const sectionIds = collectCartSectionIds();
      if (sectionIds.length) body.sections = sectionIds;

      return fetch(updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
    })
    .then(function (res) {
      if (!res) return null;
      if (res.items && typeof res.json !== 'function') return res;
      if (!res.ok) {
        return fetch('/discount/' + encodeURIComponent(code), {
          credentials: 'same-origin',
          redirect: 'follow',
        }).then(function () {
          return fetch('/cart.js', {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
          }).then(function (r) {
            return r && r.ok ? r.json() : null;
          });
        });
      }
      return res.json();
    })
    .then(function (cart) {
      if (cart && cart.items) {
        document.dispatchEvent(new DiscountUpdateEvent(cart, 'andrade-solitario-upgrade'));
      }
      return cart;
    })
    .catch(function () {
      return null;
    });
}

/**
 * @param {Response} res
 * @param {HTMLElement | null | undefined} root
 * @returns {Promise<string>}
 */
async function parseCartAddError(res, root) {
  const fallback =
    (root && root.dataset.buyNowError) ||
    'Não foi possível concluir a compra. Tente novamente.';
  try {
    const data = await res.json();
    const policy = window.AndradeInventoryPolicy;
    if (policy && typeof policy.sanitizeCartError === 'function') {
      return policy.sanitizeCartError(data.message, data.description, fallback);
    }
    const raw = String(data.description || data.message || '');
    if (/esgotou|esgotad|sold out|out of stock|não está disponível|is not available/i.test(raw)) {
      return fallback;
    }
    return raw || fallback;
  } catch (e) {
    return fallback;
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {HTMLElement} root
 */
function syncVariantInputFromForm(form, root) {
  const input = form.querySelector('[name="id"]');
  if (!input) return;

  const picker = root.querySelector('variant-picker.andrade-pdp-variant-picker');
  const script = picker ? picker.querySelector('script[type="application/json"]') : null;
  if (script && script.textContent) {
    try {
      const variant = JSON.parse(script.textContent);
      if (variant && variant.id) {
        input.value = String(variant.id);
        return;
      }
    } catch (e) {
      /* ignore */
    }
  }

  const urlVariant = new URL(window.location.href).searchParams.get('variant');
  if (urlVariant) input.value = urlVariant;
}

/**
 * @param {HTMLFormElement | null | undefined} form
 * @param {HTMLElement | null | undefined} root
 * @param {{ onBusy?: (busy: boolean) => void, syncVariant?: () => void }} [opts]
 * @returns {Promise<boolean>}
 */
async function buyNowCheckout(form, root, opts) {
  root = root || (form && form.closest('[data-md-pdp]'));
  if (!form || !root) return false;

  if (!validateFormAssociatedFields(form, root)) return false;

  if (opts && typeof opts.syncVariant === 'function') {
    opts.syncVariant();
  } else {
    syncVariantInputFromForm(form, root);
  }

  const variantInput = form.querySelector('[name="id"]');
  if (!variantInput || !variantInput.value) {
    showKitValidation(
      root,
      root.dataset.buyNowSelectOptions ||
        'Selecione as opções do produto antes de comprar.'
    );
    return false;
  }

  if (opts && typeof opts.onBusy === 'function') opts.onBusy(true);

  try {
    const built = buildBuyNowItems(form, root, variantInput.value);
    if (!built.ok || !built.items) {
      if (built.error === 'upgrade') {
        validateSolitarioUpgrade(root);
      } else {
        showKitValidation(
          root,
          root.dataset.buyNowSelectOptions ||
            'Selecione as opções do produto antes de comprar.'
        );
      }
      return false;
    }

    const addPromise = fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(buildCartAddPayload(built.items)),
    });
    openCartDrawer();

    const res = await addPromise;

    if (!res.ok) {
      closeCartDrawer();
      showKitValidation(root, await parseCartAddError(res, root));
      return false;
    }

    let cart;
    try {
      cart = await res.json();
    } catch (e) {
      cart = undefined;
    }

    document.dispatchEvent(
      new CartAddEvent(cart, 'andrade-buy-now', {
        source: 'product-form-component',
        variantId: String(variantInput.value),
        itemCount: cart && cart.item_count != null ? cart.item_count : built.items.length,
        sections: cart && cart.sections,
      })
    );

    if (hasActiveUpgrade(root)) applyUpgradeDiscountCode(root);
    if (window.AndradeGift && typeof window.AndradeGift.ensure === 'function') {
      await window.AndradeGift.ensure();
    }

    return true;
  } catch (err) {
    showKitValidation(
      root,
      root.dataset.buyNowError || 'Não foi possível concluir a compra. Tente novamente.'
    );
    return false;
  } finally {
    if (opts && typeof opts.onBusy === 'function') opts.onBusy(false);
  }
}

window.AndradePdpLineItem.syncVariantInputFromForm = syncVariantInputFromForm;
window.AndradePdpLineItem.buyNowCheckout = buyNowCheckout;

/**
 * Adiciona ao carrinho sem esvaziar nem ir ao checkout.
 * @param {HTMLFormElement | null | undefined} form
 * @param {HTMLElement | null | undefined} root
 * @param {{ onBusy?: (busy: boolean) => void, syncVariant?: () => void }} [opts]
 * @returns {Promise<boolean>}
 */
async function addToCartKeep(form, root, opts) {
  root = root || (form && form.closest('[data-md-pdp]'));
  if (!form || !root) return false;

  if (!validateFormAssociatedFields(form, root)) return false;

  if (opts && typeof opts.syncVariant === 'function') {
    opts.syncVariant();
  } else {
    syncVariantInputFromForm(form, root);
  }

  const variantInput = form.querySelector('[name="id"]');
  if (!variantInput || !variantInput.value) {
    showKitValidation(
      root,
      root.dataset.buyNowSelectOptions ||
        'Selecione as opções do produto antes de comprar.'
    );
    return false;
  }

  if (opts && typeof opts.onBusy === 'function') opts.onBusy(true);

  try {
    const built = buildBuyNowItems(form, root, variantInput.value);
    if (!built.ok || !built.items) {
      if (built.error === 'upgrade') {
        validateSolitarioUpgrade(root);
      } else {
        showKitValidation(
          root,
          root.dataset.buyNowSelectOptions ||
            'Selecione as opções do produto antes de comprar.'
        );
      }
      return false;
    }

    return buyNowCheckout(form, root, opts);
  } catch (err) {
    showKitValidation(
      root,
      root.dataset.buyNowError || 'Não foi possível concluir a compra. Tente novamente.'
    );
    return false;
  } finally {
    if (opts && typeof opts.onBusy === 'function') opts.onBusy(false);
  }
}

window.AndradePdpLineItem.addToCartKeep = addToCartKeep;

/**
 * Andrade PDP — galeria, preços, variantes (ES5-friendly, sem class/#).
 * @param {HTMLElement} root
 */
function AndradeProductPage(root) {
  this.root = root;
  this.moneyFormat = root.dataset.moneyFormat || '{{amount}}';
  this.pixDiscount = root.dataset.pixDiscount == null || root.dataset.pixDiscount === ''
    ? 0
    : Number(root.dataset.pixDiscount) / 100;
  this.installments = Number(root.dataset.installments || 10);
  this.cashbackPercent = Number(root.dataset.cashbackPercent || 0) / 100;
  this.freeShippingMin = Number(root.dataset.freeShippingMin || 29900);
  this.viewersMin = Number(root.dataset.viewersMin || 18);
  this.viewersMax = Number(root.dataset.viewersMax || 98);
  this.tplFreeShipping = root.dataset.templateFreeShipping || 'Frete grátis';
  this.tplFreeShippingFrom =
    root.dataset.templateFreeShippingFrom || 'Frete grátis a partir de __AMOUNT__';
  this.promoEnd = root.dataset.promoEnd || '';
  this.tplViewers = root.dataset.templateViewers || '__COUNT__ vendo agora';
  this.galleryMap = this.parseGalleryMap();

  this.root._andradePdp = this;

  this.bindGalleryZoom();
  this.bindVariantPickerGallery();
  this.bindBuyNow();
  this.bindAddCartBusy();
  this.bindNavScroll();
  this.bindVariantUpdates();
  this.bindFormVariantSync();
  this.bindKitValidationClear();
  this.bindSolitarioUpgrade();
  this.bindSizeGuide();

  const self = this;
  document.addEventListener('andrade:vitrine', function () {
    self.updatePrices(self.getKitVariantForPrice());
  });

  const initialPrice = Number(root.dataset.variantPrice || 0);
  const initialCompare = Number(root.dataset.variantCompare || 0);
  if (initialPrice > 0) {
    this.updatePrices({
      price: initialPrice,
      compare_at_price: initialCompare > initialPrice ? initialCompare : null,
    });
  } else {
    this.updateShippingTag(initialPrice);
  }

}

AndradeProductPage.prototype.parseGalleryMap = function () {
  const el = this.root.querySelector('[data-md-gallery-variant-map]');
  if (!el || !el.textContent) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    console.warn('[Andrade PDP] mapa da galeria inválido', e);
    return null;
  }
};

AndradeProductPage.prototype.getSelectedVariantId = function () {
  const form = this.root.querySelector('form[action*="/cart/add"]');
  if (!form) return null;
  const input = form.querySelector('[name="id"]');
  return input && input.value ? String(input.value) : null;
};

AndradeProductPage.prototype.getMaxGallerySlots = function () {
  const grid = this.root.querySelector('[data-md-gallery-grid]');
  const map = this.galleryMap;
  return (map && map.maxSlots) || Number((grid && grid.dataset.slots) || 4) || 4;
};

AndradeProductPage.prototype.resolveVisualGroupValue = function () {
  const map = this.galleryMap;
  if (!map) return null;

  const picker = this.root.querySelector('variant-picker.andrade-pdp-variant-picker');
  if (picker) {
    const visualFieldset = picker.querySelector('[data-md-visual-option="true"]');
    if (visualFieldset) {
      const visualChecked = visualFieldset.querySelector('input[type="radio"]:checked');
      if (visualChecked) return visualChecked.value?.trim() || null;
    }
  }

  const visualKey = map.visualOption || 'option1';
  const pos = visualKey === 'option2' ? 2 : visualKey === 'option3' ? 3 : 1;
  if (!picker) return null;

  for (const fieldset of picker.querySelectorAll('fieldset')) {
    const checked = fieldset.querySelector('input[type="radio"]:checked');
    if (!checked) continue;
    const inputPos = Number(checked.dataset.inputId?.split('-')[0] || 0);
    if (inputPos === pos) {
      const value = checked.value?.trim();
      return value || null;
    }
  }

  if (map.resolver?.groupsOrdered?.length === 1) {
    return map.resolver.groupsOrdered[0];
  }
  return null;
};

AndradeProductPage.prototype.getVariantImageIds = function (variant) {
  const map = this.galleryMap;
  if (!map) return [];

  let groupValue = this.resolveVisualGroupValue();

  if (!groupValue && variant && window.AndradePdpGallery) {
    groupValue = window.AndradePdpGallery.groupForVariant(
      variant,
      map.visualOption || 'option1'
    );
  }

  const visualKey = map.visualOption || 'option1';
  if (!groupValue && variant) {
    const entry =
      variant.id != null && map.variants ? map.variants[String(variant.id)] : null;
    groupValue =
      entry?.groupValue != null
        ? entry.groupValue
        : variant[visualKey] != null
          ? variant[visualKey]
          : null;
  }

  if (
    groupValue &&
    map.byVisual &&
    map.byVisual[groupValue] &&
    map.byVisual[groupValue].imageIds &&
    map.byVisual[groupValue].imageIds.length
  ) {
    return this.orderGalleryImageIds(
      map.byVisual[groupValue].imageIds.map(String),
      groupValue,
      variant
    );
  }

  if (window.AndradePdpGallery && map.resolver && groupValue) {
    const computed = window.AndradePdpGallery.computeForGroup(map, groupValue);
    if (computed?.length) return this.orderGalleryImageIds(computed, groupValue, variant);
  }

  const variantId =
    variant && variant.id != null
      ? String(variant.id)
      : this.getSelectedVariantId();

  if (!variantId || !map.variants || !map.variants[variantId]) return [];

  const entry = map.variants[variantId];
  const imageIds = entry.imageIds || entry.mediaIds || [];
  if (!imageIds || !imageIds.length) return [];
  return this.orderGalleryImageIds(imageIds.map(String), groupValue, variant);
};

AndradeProductPage.prototype.orderGalleryImageIds = function (imageIds, groupValue, variant) {
  const map = this.galleryMap;
  if (
    !imageIds?.length ||
    !groupValue ||
    !window.AndradePdpGallery?.orderGroupImageIds ||
    !map?.resolver?.images
  ) {
    return imageIds;
  }

  const include = {};
  imageIds.forEach((id) => {
    include[String(id)] = true;
  });

  return (
    window.AndradePdpGallery.orderGroupImageIds(
      map.resolver.images,
      include,
      groupValue,
      map.allVariants || [],
      map.visualOption || 'option1',
      this.getFeaturedImageId(variant)
    ) || imageIds
  );
};

AndradeProductPage.prototype.getGridImageIdsFromList = function (imageIds) {
  if (!imageIds || !imageIds.length) return [];
  const maxSlots = this.getMaxGallerySlots();
  if (window.AndradePdpGallery && typeof window.AndradePdpGallery.fillGalleryGridSlots === 'function') {
    return window.AndradePdpGallery.fillGalleryGridSlots(imageIds, maxSlots);
  }
  if (imageIds.length >= maxSlots) return imageIds.slice(0, maxSlots);
  const filled = [];
  for (let i = 0; i < maxSlots; i += 1) {
    filled.push(imageIds[i % imageIds.length]);
  }
  return filled;
};

AndradeProductPage.prototype.getGalleryImageIds = function (variant) {
  const v =
    variant ||
    this.getPickerVariant() ||
    (this.getSelectedVariantId() ? { id: this.getSelectedVariantId() } : null);
  return this.getVariantImageIds(v);
};

AndradeProductPage.prototype.getFeaturedImageId = function (variant) {
  if (!variant) return null;
  if (variant.featured_media && variant.featured_media.id != null) {
    return String(variant.featured_media.id);
  }
  if (variant.featured_image && variant.featured_image.id != null) {
    return String(variant.featured_image.id);
  }
  return null;
};

AndradeProductPage.prototype.ensureGalleryLightbox = function () {
  if (this._galleryLightbox) return this._galleryLightbox;

  const dialog = this.root.querySelector('[data-md-gallery-lightbox]');
  if (!(dialog instanceof HTMLDialogElement)) return null;

  const track = dialog.querySelector('[data-md-lightbox-track]');
  const counter = dialog.querySelector('[data-md-lightbox-counter]');
  const prev = dialog.querySelector('[data-md-lightbox-prev]');
  const next = dialog.querySelector('[data-md-lightbox-next]');

  const close = () => {
    if (dialog.open) dialog.close();
    document.body.classList.remove('md-pdp-lightbox-open');
  };

  dialog.addEventListener('close', () => {
    document.body.classList.remove('md-pdp-lightbox-open');
  });

  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    close();
  });

  dialog.querySelectorAll('[data-md-lightbox-close]').forEach((btn) => {
    btn.addEventListener('click', close);
  });

  const go = (delta) => {
    if (!this._lightboxSlideCount) return;
    this._lightboxIndex =
      (this._lightboxIndex + delta + this._lightboxSlideCount) % this._lightboxSlideCount;
    this.updateLightboxSlide();
  };

  if (prev) prev.addEventListener('click', () => go(-1));
  if (next) next.addEventListener('click', () => go(1));

  dialog.addEventListener('keydown', (e) => {
    if (!dialog.open) return;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      go(-1);
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      go(1);
    }
  });

  const viewport = dialog.querySelector('.md-pdp-lightbox__viewport');
  if (viewport) {
    let touchStartX = 0;
    viewport.addEventListener(
      'touchstart',
      (e) => {
        touchStartX = e.changedTouches[0].clientX;
      },
      { passive: true }
    );
    viewport.addEventListener(
      'touchend',
      (e) => {
        const dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) < 40) return;
        go(dx < 0 ? 1 : -1);
      },
      { passive: true }
    );
  }

  this._galleryLightbox = { dialog, track, counter, prev, next, close };
  return this._galleryLightbox;
};

AndradeProductPage.prototype.buildLightboxSlides = function (imageIds) {
  const map = this.galleryMap;
  const lb = this.ensureGalleryLightbox();
  if (!lb || !map || !map.images || !lb.track) return false;

  const slides = [];
  imageIds.forEach((id) => {
    const data = map.images[String(id)];
    if (data && data.src) {
      slides.push({ id: String(id), src: data.src, alt: data.alt || '' });
    }
  });

  if (!slides.length) return false;

  lb.track.innerHTML = '';
  slides.forEach((slide, index) => {
    const li = document.createElement('li');
    li.className = 'md-pdp-lightbox__slide';
    li.setAttribute('role', 'listitem');
    li.setAttribute('data-md-lightbox-slide', '');
    li.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    const img = document.createElement('img');
    img.src = slide.src;
    img.alt = slide.alt;
    img.loading = index === 0 ? 'eager' : 'lazy';
    li.appendChild(img);
    lb.track.appendChild(li);
  });

  this._lightboxSlideCount = slides.length;
  return true;
};

AndradeProductPage.prototype.updateLightboxSlide = function () {
  const lb = this._galleryLightbox;
  if (!lb || !lb.track) return;

  const index = this._lightboxIndex || 0;
  const total = this._lightboxSlideCount || 0;
  lb.track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;

  lb.track.querySelectorAll('[data-md-lightbox-slide]').forEach((slide, i) => {
    slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
  });

  if (lb.counter) {
    const tpl = this.root.dataset.galleryCounterTemplate || '__CURRENT__ de __TOTAL__';
    lb.counter.textContent = tpl
      .replace('__CURRENT__', String(index + 1))
      .replace('__TOTAL__', String(total));
  }

  if (lb.prev) lb.prev.disabled = total <= 1;
  if (lb.next) lb.next.disabled = total <= 1;
};

AndradeProductPage.prototype.openGalleryLightbox = function (startImageId) {
  const imageIds = this.getGalleryImageIds();
  if (!imageIds.length) return;

  if (!this.buildLightboxSlides(imageIds)) return;

  let startIndex = 0;
  if (startImageId != null) {
    const idx = imageIds.indexOf(String(startImageId));
    if (idx >= 0) startIndex = idx;
  }

  this._lightboxIndex = startIndex;
  this.updateLightboxSlide();

  const lb = this._galleryLightbox;
  if (lb && lb.dialog && !lb.dialog.open) {
    lb.dialog.showModal();
    document.body.classList.add('md-pdp-lightbox-open');
  }
};

AndradeProductPage.prototype.bindGalleryZoom = function () {
  const grid = this.root.querySelector('[data-md-gallery-grid]');
  if (!grid) return;
  grid.addEventListener('click', (e) => {
    const cell = e.target.closest('[data-md-gallery-cell]');
    if (!cell) return;
    e.preventDefault();
    const imageId = cell.getAttribute('data-image-id');
    this.openGalleryLightbox(imageId);
  });
};

AndradeProductPage.prototype.renderGalleryWithImageIds = function (gridIds, fullImageIds) {
  const grid = this.root.querySelector('[data-md-gallery-grid]');
  const map = this.galleryMap;
  const imageIds = gridIds || fullImageIds;
  if (!grid || !map || !map.images || !imageIds || !imageIds.length) return;

  const maxSlots = this.getMaxGallerySlots();
  const totalImages = (fullImageIds && fullImageIds.length) || imageIds.length;
  const moreCount = Math.max(0, totalImages - maxSlots);
  const zoomLabel = this.root.dataset.galleryZoomLabel || 'Ampliar foto __INDEX__';
  const moreBadgeTpl =
    this.root.dataset.galleryMoreBadge || '+__COUNT__';

  grid.dataset.totalImages = String(totalImages);
  grid.innerHTML = '';

  for (let slot = 0; slot < maxSlots; slot += 1) {
    const id = imageIds[slot];
    const data = id != null ? map.images[String(id)] : null;

    if (data && data.src900) {
      const btn = document.createElement('button');
      btn.type = 'button';
      const isLastWithMore = slot === maxSlots - 1 && moreCount > 0;
      btn.className = 'md-pdp__gallery-cell' + (isLastWithMore ? ' md-pdp__gallery-cell--has-more' : '');
      btn.setAttribute('role', 'listitem');
      btn.setAttribute('data-md-gallery-cell', '');
      btn.setAttribute('data-image-id', String(id));
      btn.setAttribute('aria-label', zoomLabel.replace('__INDEX__', String(slot + 1)));
      const img = document.createElement('img');
      img.className = 'md-pdp__gallery-cell-img';
      img.alt = data.alt || '';
      img.loading = slot === 0 ? 'eager' : 'lazy';
      img.removeAttribute('src');
      img.src = data.src900;
      img.sizes = '(min-width: 990px) 26vw, 46vw';
      btn.appendChild(img);
      if (isLastWithMore) {
        const badge = document.createElement('span');
        badge.className = 'md-pdp__gallery-cell-more';
        badge.setAttribute('aria-hidden', 'true');
        badge.textContent = moreBadgeTpl.replace('__COUNT__', String(moreCount));
        btn.appendChild(badge);
      }
      grid.appendChild(btn);
    } else {
      const empty = document.createElement('div');
      empty.className = 'md-pdp__gallery-cell md-pdp__gallery-cell--empty';
      empty.setAttribute('role', 'listitem');
      empty.setAttribute('aria-hidden', 'true');
      grid.appendChild(empty);
    }
  }
};

AndradeProductPage.prototype.renderGalleryFromFeaturedMedia = function (variant) {
  const variantIds = this.getVariantImageIds(variant);
  if (variantIds.length) {
    const gridIds = this.getGridImageIdsFromList(variantIds);
    this.renderGalleryWithImageIds(gridIds, variantIds);
    return true;
  }

  return false;
};

AndradeProductPage.prototype.updateGalleryFromVariant = function (variant) {
  if (!variant || !variant.id) return;

  if (this._galleryLightbox && this._galleryLightbox.dialog && this._galleryLightbox.dialog.open) {
    this._galleryLightbox.close();
  }

  const variantIds = this.getVariantImageIds(variant);
  if (variantIds.length) {
    const gridIds = this.getGridImageIdsFromList(variantIds);
    this.renderGalleryWithImageIds(gridIds, variantIds);
  }
};

AndradeProductPage.prototype.getPickerVariant = function () {
  const script = this.root.querySelector(
    'variant-picker.andrade-pdp-variant-picker script[type="application/json"]'
  );
  if (!script || !script.textContent) return null;
  try {
    return JSON.parse(script.textContent);
  } catch {
    return null;
  }
};

AndradeProductPage.prototype.bindFormVariantSync = function () {
  const form = this.root.querySelector('[data-md-product-form]');
  if (!form) return;

  form.addEventListener(
    'submit',
    (event) => {
      if (!validateFormAssociatedFields(form, this.root)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const input = form.querySelector('[name="id"]');
      if (!input) return;
      const radios = this.root.querySelectorAll(
        'variant-picker.andrade-pdp-variant-picker fieldset input[type="radio"]:checked'
      );
      if (radios.length < 2) {
        const variant = this.getPickerVariant();
        if (variant?.id) input.value = String(variant.id);
      }
    },
    true
  );
};

AndradeProductPage.prototype.bindVariantPickerGallery = function () {
  /* Galeria na troca de variante: andrade-pdp-sticky-sync.js + variant:update */
};

AndradeProductPage.prototype.setCommerceBusy = function (busy) {
  const buyNow = this.root.querySelector('[data-md-buy-now]');
  const stickyBtn = document.querySelector('[data-md-sticky-buy-now]');

  [buyNow, stickyBtn].forEach((el) => {
    if (!el) return;
    el.classList.toggle('md-pdp__btn--busy', busy);
    if (busy) el.setAttribute('aria-busy', 'true');
    else el.removeAttribute('aria-busy');
  });
};

AndradeProductPage.prototype.resetCommerceState = function () {
  this.setCommerceBusy(false);

  const buyNow = this.root.querySelector('[data-md-buy-now]');
  const stickyBtn = document.querySelector('[data-md-sticky-buy-now]');

  [buyNow, stickyBtn].forEach((el) => {
    if (!el) return;
    delete el.dataset.mdSubmitting;
    el.removeAttribute('disabled');
  });

  const variant = this.getPickerVariant();
  if (variant) {
    this.updateAvailability(variant);
    return;
  }

  const input = this.root.querySelector('[data-md-product-form] [name="id"]');
  if (input && input.value && buyNow) buyNow.removeAttribute('disabled');
};

AndradeProductPage.prototype.bindAddCartBusy = function () {
  const self = this;
  const form = this.root.querySelector('[data-md-product-form]');
  if (!form) return;

  const onDone = () => {
    self.setCommerceBusy(false);
    form.querySelectorAll('[data-md-buy-now]').forEach((el) => {
      delete el.dataset.mdSubmitting;
    });
  };

  document.addEventListener('cart:update', onDone);
  document.addEventListener('cart:error', onDone);
};

AndradeProductPage.prototype.bindKitValidationClear = function () {
  const self = this;
  const onChange = function (event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (
      target.closest(
        '[data-md-kit-sizes], [data-md-kit-extra-size], [data-md-gravacao-fields], [data-md-solitario-upgrade]'
      )
    ) {
      clearKitValidation(self.root);
    }
  };
  this.root.addEventListener('input', onChange);
  this.root.addEventListener('change', onChange);
};

AndradeProductPage.prototype.getKitVariantForPrice = function () {
  const picker = this.getPickerVariant();
  const dataPrice = Number(this.root.dataset.variantPrice || 0);
  const dataCompare = Number(this.root.dataset.variantCompare || 0);
  const pickerPrice = picker && picker.price != null ? Number(picker.price) : NaN;
  const price = pickerPrice > 0 ? pickerPrice : dataPrice;
  let compare = null;
  const pickerCompare = picker && picker.compare_at_price != null ? Number(picker.compare_at_price) : NaN;
  if (pickerCompare > price) compare = pickerCompare;
  else if (dataCompare > price) compare = dataCompare;
  return { price: price, compare_at_price: compare };
};

function percentOffFloor(cents, percent) {
  const c = Math.round(Number(cents) || 0);
  const p = Number(percent) || 0;
  if (p <= 0) return c;
  if (p >= 100) return 0;
  return Math.floor((c * (100 - p)) / 100);
}

AndradeProductPage.prototype.getUpgradeCardTotal = function (kitCents) {
  const selections = getKitUpgradeSelections(this.root);
  const active = selections.filter(function (s) {
    return s.active && !s.missing;
  });
  if (!active.length) return kitCents;
  const wrap = active[0].wrap;
  const pct = Number((wrap && wrap.getAttribute('data-discount-percent')) || 15);
  const full = active.reduce(function (sum, s) {
    return sum + (Number(s.priceCents) || 0);
  }, 0);
  return percentOffFloor(kitCents + full, pct);
};

AndradeProductPage.prototype.getUpgradeExtraCents = function () {
  const kit = Number(this.root.dataset.variantPrice || 0);
  const card = this.getUpgradeCardTotal(kit);
  const extra = card - kit;
  return extra > 0 ? extra : 0;
};

AndradeProductPage.prototype.refreshDisplayedPrices = function () {
  this.updatePrices(this.getKitVariantForPrice());

  if (window.AndradeSolitarioUpgrade && typeof window.AndradeSolitarioUpgrade.apply === 'function') {
    window.AndradeSolitarioUpgrade.apply();
  }
};

AndradeProductPage.prototype.bindSolitarioUpgrade = function () {
  const wraps = getKitUpgradeWraps(this.root);
  if (!wraps.length) return;
  const self = this;

  const sync = function () {
    window.requestAnimationFrame(function () {
      self.refreshDisplayedPrices();
    });
  };

  wraps.forEach(function (wrap) {
    wrap.addEventListener('change', function (event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      if (target.hasAttribute('data-md-solitario-upgrade-toggle') && !target.checked) {
        wrap.querySelectorAll('[data-md-solitario-upgrade-option]').forEach(function (el) {
          if (el instanceof HTMLInputElement) el.checked = false;
        });
      }
      if (
        target.hasAttribute('data-md-solitario-upgrade-toggle') ||
        target.hasAttribute('data-md-solitario-upgrade-option')
      ) {
        sync();
      }
    });

    wrap.addEventListener('click', function (event) {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const card = target.closest('.md-pdp-solitario-upgrade__card');
      if (!card || !wrap.contains(card)) return;
      const radio = card.querySelector('[data-md-solitario-upgrade-option]');
      if (!(radio instanceof HTMLInputElement)) return;
      radio.checked = true;
      const toggle = wrap.querySelector('[data-md-solitario-upgrade-toggle]');
      if (toggle instanceof HTMLInputElement) toggle.checked = true;
      sync();
    });
  });
};

AndradeProductPage.prototype.bindSizeGuide = function () {
  const dialog = this.root.querySelector('[data-md-size-guide]');
  if (!(dialog instanceof HTMLDialogElement)) return;

  const close = function () {
    if (dialog.open) dialog.close();
    document.body.classList.remove('md-pdp-size-guide-open');
  };

  const open = function () {
    if (!dialog.open) dialog.showModal();
    document.body.classList.add('md-pdp-size-guide-open');
  };

  this.root.addEventListener('click', function (event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const opener = target.closest('[data-md-size-guide-open]');
    if (!opener) return;
    event.preventDefault();
    open();
  });

  dialog.querySelectorAll('[data-md-size-guide-close]').forEach(function (btn) {
    btn.addEventListener('click', close);
  });
  dialog.addEventListener('close', function () {
    document.body.classList.remove('md-pdp-size-guide-open');
  });
};

AndradeProductPage.prototype.bindBuyNow = function () {
  const self = this;
  const btn = this.root.querySelector('[data-md-buy-now]');
  const form = this.root.querySelector('[data-md-product-form]');
  if (!btn || !form) return;

  btn.addEventListener('click', async (e) => {
    e.preventDefault();
    if (btn.disabled || btn.dataset.mdSubmitting === 'true' || btn.classList.contains('md-pdp__btn--busy')) {
      return;
    }

    btn.dataset.mdSubmitting = 'true';
    const ok = await buyNowCheckout(form, self.root, {
      syncVariant: () => {
        syncVariantInputFromForm(form, self.root);
        const variant = self.getPickerVariant();
        if (variant?.id) {
          const input = form.querySelector('[name="id"]');
          if (input) input.value = String(variant.id);
        }
      },
      onBusy: (busy) => {
        if (busy) self.setCommerceBusy(true);
        else self.resetCommerceState();
      },
    });
    delete btn.dataset.mdSubmitting;
    if (!ok) self.resetCommerceState();
  });
};

AndradeProductPage.prototype.bindNavScroll = function () {
  const links = this.root.querySelectorAll('[data-md-anchor]');
  links.forEach((link) => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');
      if (!href || href.indexOf('#') !== 0) return;
      const target = document.querySelector(href);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      links.forEach((l) => l.classList.remove('is-active'));
      link.classList.add('is-active');
    });
  });
};

AndradeProductPage.prototype.bindVariantUpdates = function () {
  const self = this;
  const productId = String(this.root.dataset.productId || '');

  const handler = (event) => {
    const variant = event.detail && event.detail.resource;
    const eventProductId = event.detail && event.detail.data && event.detail.data.productId;

    if (eventProductId && String(eventProductId) !== productId) return;

    if (variant && variant.price != null) {
      self.root.dataset.variantPrice = String(variant.price);
      if (variant.compare_at_price != null) {
        self.root.dataset.variantCompare = String(variant.compare_at_price);
      }
      self.refreshDisplayedPrices();
    }
    if (variant && variant.id) {
      self.updateAvailability(variant);
    }
  };

  const section = this.root.closest('.shopify-section');
  if (section) section.addEventListener(ThemeEvents.variantUpdate, handler);
  document.addEventListener(ThemeEvents.variantUpdate, handler);
};

AndradeProductPage.prototype.updatePrices = function (variant) {
  const kit = variant && variant.price != null ? Number(variant.price) : Number(this.root.dataset.variantPrice || 0);
  if (!(kit >= 0)) return;

  const selections = getKitUpgradeSelections(this.root);
  const activeUpgrades = selections.filter(function (s) {
    return s.active && !s.missing;
  });
  const upgradeOn = activeUpgrades.length > 0;
  const fullUpgrade = activeUpgrades.reduce(function (sum, s) {
    return sum + (Number(s.priceCents) || 0);
  }, 0);
  const compareList = upgradeOn ? kit + fullUpgrade : kit;
  let price = this.getUpgradeCardTotal(kit);
  const autoPct = Number(this.root.dataset.autoDiscountPercent || 0);
  let compare = upgradeOn && compareList > price ? compareList : null;
  if (autoPct > 0 && !upgradeOn) {
    compare = kit;
    price = percentOffFloor(kit, autoPct);
  }
  const pixPercent = Math.round(this.pixDiscount * 100);
  const pix = percentOffFloor(price, pixPercent);
  const pixSaved = price - pix;
  const showPixDeal = pixPercent > 0 && pixSaved > 0;
  const installment = Math.floor(
    (price + Math.floor(this.installments / 2)) / Math.max(1, this.installments)
  );
  const savings = compare ? compare - price : 0;
  const onSale = Boolean(compare && compare > price);
  const showPromoPrice = autoPct > 0 && !upgradeOn;

  const compareEls = this.root.querySelectorAll('[data-md-compare]');
  const pixEls = this.root.querySelectorAll('[data-md-pix]');
  const pixSaveEls = this.root.querySelectorAll('[data-md-pix-savings]');
  const pixBlocks = this.root.querySelectorAll('[data-md-pix-block]');
  const instEls = this.root.querySelectorAll('[data-md-installments]');
  const saveEl = this.root.querySelector('[data-md-savings]');
  const badgeEl = this.root.querySelector('[data-md-discount-badge]');
  const dropWrap = this.root.querySelector('.md-pdp__offer-tags');
  const dropEl = this.root.querySelector('[data-md-price-drop]');
  const shippingTag = this.root.querySelector('[data-md-shipping-tag]');
  const promoOff = this.root.querySelector('[data-md-promo-off]');
  const promoBar = this.root.querySelector('[data-md-promo-bar]');

  const promoLine = this.root.querySelector('[data-md-promo-line]');
  const promoPctEl = this.root.querySelector('[data-md-promo-pct]');
  const comparePlain = this.root.querySelector('[data-md-compare-plain]');

  pixBlocks.forEach((block) => {
    block.hidden = !showPixDeal;
  });
  const autoPriceEl = this.root.querySelector('[data-md-auto-price]');
  if (autoPriceEl) {
    if (showPromoPrice) {
      autoPriceEl.classList.remove('hidden');
      autoPriceEl.textContent = formatMoney(price, this.moneyFormat);
    } else {
      autoPriceEl.classList.add('hidden');
    }
  }
  const listPriceEl = this.root.querySelector('[data-md-list-price]');
  if (listPriceEl) {
    if (!showPromoPrice && !showPixDeal) {
      listPriceEl.classList.remove('hidden');
      listPriceEl.textContent = formatMoney(price, this.moneyFormat);
    } else {
      listPriceEl.classList.add('hidden');
      listPriceEl.textContent = '';
    }
  }
  const promoName = this.root.querySelector('[data-md-promo-name]');
  const campaign = (promoName?.getAttribute('data-andrade-promo-campaign') || '').trim();
  if (promoLine) {
    promoLine.classList.toggle('hidden', !showPromoPrice && !campaign);
  }
  if (promoName) {
    promoName.textContent = campaign;
    promoName.hidden = !campaign;
    const dash = this.root.querySelector('.md-pdp__promo-dash');
    if (dash) dash.hidden = !showPromoPrice || !campaign;
  }
  if (promoPctEl) {
    const campaign = (
      this.root.querySelector('[data-md-promo-name]')?.getAttribute('data-andrade-promo-campaign') || ''
    ).trim();
    const showPct = showPromoPrice && autoPct >= 1 && campaign.length > 0;
    promoPctEl.hidden = !showPct;
    if (showPct) promoPctEl.textContent = autoPct + '% OFF';
  }
  pixEls.forEach((pixEl) => {
    if (showPromoPrice) {
      pixEl.classList.add('hidden');
      return;
    }
    pixEl.classList.remove('hidden');
    const tpl = pixEl.dataset.template || '';
    pixEl.textContent = tpl.replace('__PRICE__', formatMoney(pix, this.moneyFormat));
  });
  pixSaveEls.forEach((el) => {
    if (!showPixDeal) {
      el.hidden = true;
      return;
    }
    el.hidden = false;
    const pixPct = Math.round(this.pixDiscount * 100);
    const tpl = showPromoPrice
      ? el.dataset.templatePromo || el.dataset.template || ''
      : el.dataset.template || '';
    el.textContent = tpl
      .replace('__AMOUNT__', formatMoney(pixSaved, this.moneyFormat))
      .replace('__PERCENT__', String(pixPct));
  });
  instEls.forEach((instEl) => {
    const tpl = instEl.dataset.template || '';
    instEl.textContent = tpl
      .replace('__COUNT__', String(this.installments))
      .replace('__PRICE__', formatMoney(installment, this.moneyFormat));
  });

  if (onSale) {
    compareEls.forEach((compareEl) => {
      compareEl.classList.remove('hidden');
      compareEl.textContent = formatMoney(compare, this.moneyFormat);
    });
    if (saveEl) {
      saveEl.classList.remove('hidden');
      const saveValue = saveEl.querySelector('[data-md-savings-value]');
      if (saveValue) saveValue.textContent = formatMoney(savings, this.moneyFormat);
    }
    if (dropEl) dropEl.classList.remove('hidden');
    if (dropWrap) dropWrap.classList.remove('hidden');
    const pct = Math.round((savings * 100) / compare);
    if (badgeEl) {
      const tpl = badgeEl.dataset.template || '__PCT__% OFF';
      badgeEl.textContent = tpl.replace('__PCT__', String(pct)).replace('{{ percent }}', String(pct));
      badgeEl.classList.remove('hidden');
    }
    if (promoOff) promoOff.textContent = pct + '% OFF';
    if (promoBar) {
      const startAt = this.root.dataset.promoStart || '';
      const endAt = this.root.dataset.promoEnd || '';
      const showBar = Boolean(showPromoPrice && startAt && endAt);
      promoBar.hidden = !showBar;
      promoBar.classList.toggle('hidden', !showBar);
    }
  } else {
    compareEls.forEach(function (compareEl) {
      compareEl.classList.add('hidden');
    });
    if (saveEl) saveEl.classList.add('hidden');
    if (dropEl) dropEl.classList.add('hidden');
    if (dropWrap) {
      if (shippingTag && !shippingTag.classList.contains('hidden')) {
        dropWrap.classList.remove('hidden');
      } else {
        dropWrap.classList.add('hidden');
      }
    }
    if (badgeEl) badgeEl.classList.add('hidden');
    if (promoBar) {
      promoBar.hidden = true;
      promoBar.classList.add('hidden');
    }
  }

  if (comparePlain) {
    if (onSale && !showPromoPrice) {
      comparePlain.classList.remove('hidden');
      comparePlain.textContent = formatMoney(compare, this.moneyFormat);
    } else {
      comparePlain.classList.add('hidden');
    }
  }

  this.updateShippingTag(price);

  const cashbackEl = this.root.querySelector('[data-md-cashback]');
  if (cashbackEl && this.cashbackPercent > 0) {
    const cb = Math.round(price * this.cashbackPercent);
    const tpl = cashbackEl.dataset.template || '';
    cashbackEl.textContent = tpl.replace('__AMOUNT__', formatMoney(cb, this.moneyFormat));
  }

  const stickyPrice = document.querySelector('[data-md-sticky-price]');
  if (stickyPrice) {
    if (showPromoPrice && autoPriceEl && autoPriceEl.textContent) {
      stickyPrice.textContent = autoPriceEl.textContent;
    } else {
      const firstPix = pixEls[0];
      if (firstPix && firstPix.textContent) stickyPrice.textContent = firstPix.textContent;
    }
  }
};

AndradeProductPage.prototype.updateShippingTag = function (priceCents) {
  const tag = this.root.querySelector('[data-md-shipping-tag]');
  const text = tag ? tag.querySelector('[data-md-shipping-text]') : null;
  if (!tag || !text) return;

  const pix = Math.floor(priceCents * (1 - this.pixDiscount));
  const qualifies = pix >= this.freeShippingMin;

  text.textContent = qualifies
    ? this.tplFreeShipping
    : this.tplFreeShippingFrom.replace('__AMOUNT__', formatMoney(this.freeShippingMin, this.moneyFormat));
};

AndradeProductPage.prototype.resolveMaterialFromVariant = function (variant) {
  if (!variant) return '';
  const raw = this.root.dataset.productOptions || '';
  if (!raw) return '';
  const names = raw.split('||');
  const values = [variant.option1, variant.option2, variant.option3];
  for (let i = 0; i < names.length; i += 1) {
    const name = (names[i] || '').toLowerCase();
    const value = values[i];
    if (!value) continue;
    if (name.includes('material') || name.includes('metal') || name === 'ouro' || name === 'prata') {
      return value;
    }
  }
  return '';
};

AndradeProductPage.prototype.updateMaterial = function (variant) {
  const nameEl = this.root.querySelector('[data-md-material-name]');
  if (!nameEl) return;
  const fromVariant = this.resolveMaterialFromVariant(variant);
  if (!fromVariant) return;
  nameEl.textContent = fromVariant;
  const a11y = this.root.querySelector('[data-md-material-a11y]');
  if (a11y) {
    const tpl = a11y.dataset.template || '';
    a11y.textContent = tpl.indexOf('__MATERIAL__') >= 0 ? tpl.replace('__MATERIAL__', fromVariant) : fromVariant;
  }
};

AndradeProductPage.prototype.updateAvailability = function (variant) {
  const meta = this.root.querySelector('[data-md-stock]');
  const buyNow = this.root.querySelector('[data-md-buy-now]');
  const notify = this.root.querySelector('[data-md-notify]');
  const buyNowLabel = this.root.dataset.labelBuyNow || 'Comprar agora';
  const policy = window.AndradeInventoryPolicy;

  if (meta && policy) {
    policy.applyStockMeta(meta, variant);
  }
  if (buyNow) {
    buyNow.disabled = false;
    buyNow.textContent = buyNowLabel;
  }
  if (notify) notify.classList.add('hidden');
  this.updateMaterial(variant);
};

AndradeProductPage.prototype.parsePromoEnd = function (raw) {
  const target = (raw || '').trim();
  if (!target) return 0;
  const normalized = target.indexOf('T') >= 0 ? target : target + 'T23:59:59';
  let end = Date.parse(normalized.replace(' ', 'T'));
  if (Number.isNaN(end)) end = Date.parse(target);
  return Number.isNaN(end) ? 0 : end;
};

AndradeProductPage.prototype.initCountdown = function () {
  const wrap = this.root.querySelector('[data-md-countdown]');
  if (!wrap) return;

  const els = {
    d: wrap.querySelector('[data-cd-days]'),
    h: wrap.querySelector('[data-cd-hours]'),
    m: wrap.querySelector('[data-cd-minutes]'),
    s: wrap.querySelector('[data-cd-seconds]'),
  };

  const start = this.parsePromoEnd(wrap.dataset.start || '');
  const end = this.parsePromoEnd(wrap.dataset.target || this.promoEnd);
  const bar = wrap.closest('.md-pdp__promo-bar');
  if (!start || !end || end <= start) {
    if (bar) bar.hidden = true;
    return;
  }
  const pad = (n) => String(n).padStart(2, '0');

  const tick = () => {
    const now = Date.now();
    if (now < start || now >= end) {
      if (bar) bar.hidden = true;
      return;
    }
    const diff = Math.max(0, end - now);
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff / 3600000) % 24);
    const m = Math.floor((diff / 60000) % 60);
    const s = Math.floor((diff / 1000) % 60);
    if (els.d) els.d.textContent = pad(d);
    if (els.h) els.h.textContent = pad(h);
    if (els.m) els.m.textContent = pad(m);
    if (els.s) els.s.textContent = pad(s);

    const daysWrap = wrap.querySelector('[data-cd-days-wrap]');
    const daysSep = wrap.querySelector('.countdown-bar__sep--days');
    const showDays = d > 0;
    if (daysWrap) daysWrap.classList.toggle('hidden', !showDays);
    if (daysSep) daysSep.classList.toggle('hidden', !showDays);
  };

  tick();
  window.setInterval(tick, 1000);
};

AndradeProductPage.prototype.initViewers = function () {
  const wrap = this.root.querySelector('[data-md-viewers]');
  const text = wrap ? wrap.querySelector('[data-md-viewers-text]') : null;
  if (!text) return;

  const pick = () => {
    const min = Math.min(this.viewersMin, this.viewersMax);
    const max = Math.max(this.viewersMin, this.viewersMax);
    const count = Math.floor(Math.random() * (max - min + 1)) + min;
    text.textContent = this.tplViewers.replace('__COUNT__', String(count));
  };

  if (!text.textContent.trim()) pick();
  window.setInterval(pick, 12000 + Math.random() * 8000);
};

function initAndradeProductPage() {
  const hash = (window.location.hash || '').replace('#', '');
  const hasContentAnchor =
    hash === 'md-pdp-ficha' || hash === 'md-pdp-descricao' || hash === 'md-pdp-detalhes';
  if (hasContentAnchor) {
    const target = document.getElementById(hash);
    if (target instanceof HTMLDetailsElement) {
      target.open = true;
    }
    requestAnimationFrame(() => {
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  document.querySelectorAll('[data-md-pdp]').forEach((root) => {
    if (root instanceof HTMLElement && !root._andradePdp) {
      new AndradeProductPage(root);
    }
  });

  if (window.AndradePdpCommerce && typeof window.AndradePdpCommerce.hardReset === 'function') {
    window.AndradePdpCommerce.hardReset();
  }
}

window.AndradePdpCommerce = window.AndradePdpCommerce || {};
window.AndradePdpCommerce.resetInstances = function () {
  document.querySelectorAll('[data-md-pdp]').forEach((root) => {
    if (root._andradePdp && typeof root._andradePdp.resetCommerceState === 'function') {
      root._andradePdp.resetCommerceState();
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAndradeProductPage);
} else {
  initAndradeProductPage();
}
